// src/components/catalog/ItemManager.jsx
import React, { useState, useEffect } from 'react';
import { Plus, X, Search, Filter, Upload, Package, Download, Grid, List } from 'lucide-react';
import { catalogService } from '../../services/catalogService';
import { ItemForm } from './ItemForm';
import { UniversalItemCard } from '../templates/ItemCard';

export const ItemManager = ({ catalogId, catalog }) => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'active'
  });

  useEffect(() => {
    loadItems();
  }, [catalogId]);

  useEffect(() => {
    applyFiltersAndSearch();
  }, [items, searchTerm, filters]);

  const loadItems = async () => {
    try {
      const itemsData = await catalogService.getItems(catalogId);
      setItems(itemsData);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = async () => {
    try {
      const filtered = await catalogService.searchItems(catalogId, searchTerm, filters);
      setFilteredItems(filtered);
    } catch (error) {
      console.error('Error filtering items:', error);
      setFilteredItems(items);
    }
  };

  const handleDuplicateItem = async (itemId) => {
    try {
      const item = await catalogService.getItem(itemId);
      const { id, createdAt, updatedAt, ...itemData } = item;
      
      const duplicatedItem = {
        ...itemData,
        name: `${itemData.name} (Copy)`,
        status: 'draft'
      };

      await catalogService.addItem(catalogId, duplicatedItem);
      await loadItems();
      alert('Item duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating item:', error);
      alert('Failed to duplicate item');
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (editingItem) {
        await catalogService.updateItem(editingItem.id, itemData);
      } else {
        await catalogService.addItem(catalogId, itemData);
      }
      
      await loadItems();
      setShowForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await catalogService.deleteItem(itemId, catalogId);
        await loadItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  };

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const csvText = await file.text();
      const items = catalogService.parseCSV(csvText);
      
      if (window.confirm(`Import ${items.length} items?`)) {
        await catalogService.bulkImportItems(catalogId, items);
        await loadItems();
        alert('Items imported successfully!');
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Failed to import CSV');
    }
  };

  const handleCSVExport = async () => {
    try {
      await catalogService.exportToCSV(catalogId);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Items Management</h2>
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2">
            <Package className="h-4 w-4" />
            {filteredItems.length} Items
            {filteredItems.length !== items.length && (
              <span className="opacity-80">of {items.length}</span>
            )}
          </div>
          <p className="text-gray-600">Manage your catalog items</p>
        </div>

        <div className="flex gap-2">
          <input
            type="file"
            accept=".csv"
            onChange={handleCSVImport}
            className="hidden"
            id="csv-import"
          />
          <label
            htmlFor="csv-import"
            className="btn-secondary flex items-center gap-2 cursor-pointer"
          >
            <Upload className="h-4 w-4" />
            Import CSV
          </label>
          
          <button
            onClick={handleCSVExport}
            className="btn-secondary flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>

          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>

        <div className="flex gap-2">
          <select
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
            className="input-field"
          >
            <option value="all">All Categories</option>
            {catalogService.getUniqueValues(items, 'category').map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Items Grid/List */}
      <div className={viewMode === 'grid' 
        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" 
        : "space-y-4"
      }>
        {filteredItems.map((item) => (
          <UniversalItemCard
            key={item.id}
            item={item}
            catalog={catalog}
            viewMode={viewMode}
            showEditControls={true}
            onEdit={() => {
              setEditingItem(item);
              setShowForm(true);
            }}
            onDelete={() => handleDeleteItem(item.id)}
            onDuplicate={() => handleDuplicateItem(item.id)}
          />
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No items found</p>
        </div>
      )}

      {/* Item Form Modal */}
      {showForm && (
        <ItemForm
          item={editingItem}
          onSave={handleSaveItem}
          onClose={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          catalog={catalog}
        />
      )}
    </div>
  );
};